import { useRef, useCallback } from 'react';
import type { ViewBlockHandle } from '../../core/types';
import { BlockEventType } from '../../core/constants';
import { useEventScope } from '../useEventScope';
import { useBlockHandle } from '../useBlockHandle';

interface UseViewEventAdapterOptions {
  onExternalRefetch?: () => void;
  getData?: () => Record<string, unknown> | null;
}

export function useViewEventAdapter(
  semanticId: string | undefined,
  options?: UseViewEventAdapterOptions,
) {
  const scope = useEventScope();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handleFactory = useCallback((): ViewBlockHandle => {
    return {
      semanticId: semanticId!,
      blockType: 'view',
      capabilities: new Set(['refetch']),
      refetch: () => optionsRef.current?.onExternalRefetch?.(),
      getData: () => optionsRef.current?.getData?.() ?? null,
      dispose: () => {},
    };
  }, [semanticId]);

  useBlockHandle(semanticId, handleFactory, []);

  const publishViewLoaded = useCallback(
    (data: Record<string, unknown>) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.VIEW_LOADED, semanticId, { data });
    },
    [scope, semanticId],
  );

  return {
    publishViewLoaded,
    scope,
  };
}
